import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Download, ChevronDown, LogOut, User, Globe } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/ui/Logo';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { useAuth } from '@/context/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { logOut } from '@/services/auth';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from '@/i18n/config';

interface HeaderProps {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}

export function Header({ theme, toggleTheme }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const currentLang = getCurrentLanguage();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.features'), href: '/features' },
    { name: t('nav.enterprise'), href: '/enterprise' },
    { name: t('nav.pricing'), href: '/pricing' },
    {
      name: t('nav.resources'),
      href: '#',
      children: [
        { name: t('nav.changelog'), href: '/changelog' },
        { name: t('nav.documentation'), href: '/docs' },
        { name: t('nav.blog'), href: '/blog' },
        { name: t('nav.community'), href: '/community' },
      ],
    },
    { name: t('nav.contact'), href: '/contact' },
  ];

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setResourcesOpen(false);
    setUserMenuOpen(false);
    setLanguageOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await logOut();
      setUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <nav className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/">
          <Logo size={26} />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navigation.map((item) => (
            <div key={item.name} className="relative">
              {item.children ? (
                <button
                  className={cn(
                    'nav-link flex items-center gap-1',
                    resourcesOpen && 'text-accent'
                  )}
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  onBlur={() => setTimeout(() => setResourcesOpen(false), 200)}
                >
                  {item.name}
                  <ChevronDown size={16} className={cn(
                    'transition-transform',
                    resourcesOpen && 'rotate-180'
                  )} />
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    'nav-link',
                    location.pathname === item.href && 'active'
                  )}
                >
                  {item.name}
                </Link>
              )}

              {/* Dropdown */}
              {item.children && resourcesOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 py-2 rounded-lg glass shadow-xl">
                  {item.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.href}
                      className="block px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-accent-glow transition-colors"
                      onClick={() => setResourcesOpen(false)}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative">
            <button
              onClick={() => setLanguageOpen(!languageOpen)}
              className="flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-accent-glow transition-colors"
              aria-label="Change language"
            >
              <FlagIcon code={currentLang.flagCode} size={20} />
              <ChevronDown size={14} className={cn(
                'text-text-secondary transition-transform',
                languageOpen && 'rotate-180'
              )} />
            </button>
            {languageOpen && (
              <div className="absolute right-0 top-full mt-2 w-40 py-2 rounded-lg glass shadow-xl border border-border-default">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setLanguageOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                      i18n.language === lang.code
                        ? 'text-accent bg-accent-glow'
                        : 'text-text-secondary hover:text-text-primary hover:bg-accent-glow'
                    )}
                  >
                    <FlagIcon code={lang.flagCode} size={18} />
                    <span>{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-accent-glow transition-colors"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun size={20} className="text-text-secondary" />
            ) : (
              <Moon size={20} className="text-text-secondary" />
            )}
          </button>

          {/* Sign In / User Menu */}
          {isAuthenticated && user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent-glow transition-colors"
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                )}
                <span className="text-sm text-text-primary hidden xl:block">
                  {user.displayName || user.email?.split('@')[0]}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 py-2 rounded-lg glass shadow-xl border border-border-default">
                  <div className="px-4 py-2 border-b border-border-default">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-accent-glow transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <User size={16} />
                    {t('nav.dashboard')}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                  >
                    <LogOut size={16} />
                    {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={openAuthModal}
              className="btn btn-ghost text-sm"
            >
              {t('nav.signIn')}
            </button>
          )}

          {/* Download */}
          <Link to="/download" className="btn btn-primary btn-sm">
            <Download size={16} />
            {t('nav.download')}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X size={24} className="text-text-primary" />
          ) : (
            <Menu size={24} className="text-text-primary" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden glass border-t border-border-default">
          <div className="container py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.children ? (
                  <>
                    <button
                      className="w-full text-left nav-link"
                      onClick={() => setResourcesOpen(!resourcesOpen)}
                    >
                      {item.name}
                    </button>
                    {resourcesOpen && (
                      <div className="pl-4 mt-2 space-y-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block nav-link text-sm"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setResourcesOpen(false);
                            }}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'block nav-link',
                      location.pathname === item.href && 'active'
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            <div className="pt-4 border-t border-border-default space-y-3">
              {/* Language Switcher for Mobile */}
              <div className="flex items-center gap-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors',
                      i18n.language === lang.code
                        ? 'bg-accent text-white'
                        : 'bg-accent-glow text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <FlagIcon code={lang.flagCode} size={18} />
                    <span>{lang.nativeName}</span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg hover:bg-accent-glow"
                >
                  {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                {isAuthenticated && user ? (
                  <>
                    <Link to="/dashboard" className="btn btn-ghost btn-sm flex-1">
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="btn btn-ghost btn-sm flex-1 text-error"
                    >
                      {t('nav.signOut')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      openAuthModal();
                      setMobileMenuOpen(false);
                    }}
                    className="btn btn-ghost btn-sm flex-1"
                  >
                    {t('nav.signIn')}
                  </button>
                )}
                <Link to="/download" className="btn btn-primary btn-sm flex-1" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.download')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
